import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiUser,
  FiMail,
  FiPhone,
  FiKey,
  FiEdit,
  FiSave,
  FiX,
  FiShield,
  FiLock,
  FiUnlock,
  FiCheckCircle,
  FiAlertCircle,
  FiEye,
  FiEyeOff
} from 'react-icons/fi';
import {
  FaUserCircle,
  FaEnvelope,
  FaPhone,
  FaKey,
  FaEdit,
  FaSave,
  FaTimes,
  FaShieldAlt,
  FaLock,
  FaUnlock,
  FaCheck,
  FaGem
} from 'react-icons/fa';
import { useForm } from 'react-hook-form';
import { sendApiRequest } from '../../utils';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';

const Profile = () => {
  const userData = useSelector((state) => state.user_info[0]?.user);
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    defaultValues: {
      customerName: userData?.customerName || '',
      customerEmail: userData?.customerEmail || '',
      customerPhone: userData?.customerPhone || ''
    }
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
    reset: resetPassword,
    watch
  } = useForm();

  const newPassword = watch('newPassword');

  const onSubmit = async (data) => {
    try {
      const response = await sendApiRequest(`api/customers/profile/update/${userData._id}`, data, 'PUT');
      if (response.data.success) {
        Swal.fire({
          title: 'Success!',
          text: 'Profile updated successfully',
          icon: 'success',
          confirmButtonColor: '#6366f1',
          timer: 1500,
          timerProgressBar: true
        });
        setIsEditing(false);
      }
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to update profile',
        icon: 'error',
        confirmButtonColor: '#ef4444'
      });
    }
  };

  const onPasswordSubmit = async (data) => {
    try {
      const response = await sendApiRequest(`api/customers/profile/change-password/${userData._id}`, data, 'PUT');
      if (response.data.success) {
        Swal.fire({
          title: 'Password Changed!',
          text: 'Your password has been updated successfully',
          icon: 'success',
          confirmButtonColor: '#6366f1',
          timer: 1500,
          timerProgressBar: true
        });
        setIsChangingPassword(false);
        resetPassword();
      }
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to change password',
        icon: 'error',
        confirmButtonColor: '#ef4444'
      });
    }
  };

  const inputClasses = "w-full px-4 py-3 rounded-xl border transition-all duration-200 outline-none bg-gray-50/50 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200";
  const labelClasses = "block text-sm font-semibold text-gray-700 mb-2";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-6 sm:py-10">
      <div className="container mx-auto px-4 max-w-3xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 shadow-lg mb-4">
            <FaUserCircle className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            My Profile
          </h1>
          <p className="text-gray-500 mt-2 text-sm">Manage your account information</p>
        </div>

        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 mb-6"
        >
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-xl">
                  <FiUser className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-white font-semibold text-lg">Profile Information</h2>
                  <p className="text-indigo-100 text-sm">Update your personal details</p>
                </div>
              </div>
              <button
                onClick={() => setIsEditing(!isEditing)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  isEditing 
                    ? 'bg-red-500/20 text-red-200 hover:bg-red-500/30' 
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                {isEditing ? <FiX className="w-4 h-4" /> : <FiEdit className="w-4 h-4" />}
                {isEditing ? 'Cancel' : 'Edit Profile'}
              </button>
            </div>
          </div>

          <div className="p-6">
            {isEditing ? (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                <div>
                  <label className={labelClasses}>
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      {...register('customerName', { required: 'Name is required' })}
                      className={`${inputClasses} pl-10 ${errors.customerName ? 'border-red-500' : ''}`}
                      placeholder="Enter your full name"
                    />
                  </div>
                  {errors.customerName && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <FiAlertCircle className="w-3 h-3" />
                      {errors.customerName.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className={labelClasses}>
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="email"
                      {...register('customerEmail', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address'
                        }
                      })}
                      className={`${inputClasses} pl-10 ${errors.customerEmail ? 'border-red-500' : ''}`}
                      placeholder="Enter your email address"
                    />
                  </div>
                  {errors.customerEmail && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <FiAlertCircle className="w-3 h-3" />
                      {errors.customerEmail.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className={labelClasses}>
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <FiPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="tel"
                      {...register('customerPhone', {
                        required: 'Phone number is required',
                        pattern: { 
                          value: /^[0-9]{10}$/, 
                          message: 'Invalid phone number - must be 10 digits' 
                        }
                      })}
                      className={`${inputClasses} pl-10 ${errors.customerPhone ? 'border-red-500' : ''}`}
                      placeholder="Enter your 10-digit phone number"
                    />
                  </div>
                  {errors.customerPhone && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <FiAlertCircle className="w-3 h-3" />
                      {errors.customerPhone.message}
                    </p>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-6 py-2 text-sm font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition"
                  >
                    <FiSave className="w-4 h-4" />
                    Save Changes
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-5">
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <FiUser className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Full Name</p>
                    <p className="text-lg font-semibold text-gray-800">{userData?.customerName || 'N/A'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <FiMail className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Email Address</p>
                    <p className="text-lg font-semibold text-gray-800 break-all">{userData?.customerEmail || 'N/A'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <FiPhone className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Phone Number</p>
                    <p className="text-lg font-semibold text-gray-800">{userData?.customerPhone || 'N/A'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Change Password Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
        >
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-xl">
                  <FiShield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-white font-semibold text-lg">Security Settings</h2>
                  <p className="text-indigo-100 text-sm">Change your password</p>
                </div>
              </div>
              <button
                onClick={() => setIsChangingPassword(!isChangingPassword)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  isChangingPassword 
                    ? 'bg-red-500/20 text-red-200 hover:bg-red-500/30' 
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                {isChangingPassword ? <FiX className="w-4 h-4" /> : <FiKey className="w-4 h-4" />}
                {isChangingPassword ? 'Cancel' : 'Change Password'}
              </button>
            </div>
          </div>

          <div className="p-6">
            {isChangingPassword && (
              <motion.form
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                onSubmit={handlePasswordSubmit(onPasswordSubmit)}
                className="space-y-5"
              >
                <div>
                  <label className={labelClasses}>
                    Current Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      {...registerPassword('currentPassword', { 
                        required: 'Current password is required' 
                      })}
                      className={`${inputClasses} pl-10 pr-10 ${passwordErrors.currentPassword ? 'border-red-500' : ''}`}
                      placeholder="Enter your current password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                    >
                      {showCurrentPassword ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                    </button>
                  </div>
                  {passwordErrors.currentPassword && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <FiAlertCircle className="w-3 h-3" />
                      {passwordErrors.currentPassword.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className={labelClasses}>
                    New Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <FiUnlock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      {...registerPassword('newPassword', {
                        required: 'New password is required',
                        minLength: { 
                          value: 6, 
                          message: 'Password must be at least 6 characters' 
                        }
                      })}
                      className={`${inputClasses} pl-10 pr-10 ${passwordErrors.newPassword ? 'border-red-500' : ''}`}
                      placeholder="Enter your new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                    >
                      {showNewPassword ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                    </button>
                  </div>
                  {passwordErrors.newPassword && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <FiAlertCircle className="w-3 h-3" />
                      {passwordErrors.newPassword.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className={labelClasses}>
                    Confirm New Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <FiShield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      {...registerPassword('confirmPassword', {
                        required: 'Please confirm your password',
                        validate: (value) => value === newPassword || 'Passwords do not match'
                      })}
                      className={`${inputClasses} pl-10 pr-10 ${passwordErrors.confirmPassword ? 'border-red-500' : ''}`}
                      placeholder="Confirm your new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                    >
                      {showConfirmPassword ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                    </button>
                  </div>
                  {passwordErrors.confirmPassword && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <FiAlertCircle className="w-3 h-3" />
                      {passwordErrors.confirmPassword.message}
                    </p>
                  )}
                  {newPassword && newPassword === watch('confirmPassword') && (
                    <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                      <FiCheckCircle className="w-3 h-3" />
                      Passwords match
                    </p>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setIsChangingPassword(false)}
                    className="px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-6 py-2 text-sm font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition"
                  >
                    <FiLock className="w-4 h-4" />
                    Update Password
                  </button>
                </div>
              </motion.form>
            )}

            {!isChangingPassword && (
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <FiShield className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-500">Password Status</p>
                  <p className="text-sm font-medium text-gray-800">Last updated: {userData?.updatedAt ? new Date(userData.updatedAt).toLocaleDateString() : 'Never'}</p>
                </div>
                <button
                  onClick={() => setIsChangingPassword(true)}
                  className="text-sm text-indigo-600 font-medium hover:text-indigo-800"
                >
                  Change
                </button>
              </div>
            )}
          </div>
        </motion.div>

        {/* Security Note */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-400 flex items-center justify-center gap-1">
            <FiShield className="w-3 h-3" />
            Your profile information is securely stored and encrypted
          </p>
        </div>
      </div>
    </div>
  );
};

export default Profile;